import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient } from "@/shared/services/queryClient";
import {
  sowingInstructionsApi,
  type CreateSowingInstructionRequest,
  type CreateBatchSowingInstructionRequest,
  type ApproveSowingInstructionRequest,
  type RevokeSowingInstructionRequest,
  type RecordSowingRequest,
  type RecordGerminationRequest,
  type RecordGrowthRequest,
  type RecordReadyForDispatchRequest,
  type RecordHarvestRequest,
  type MarkOvergrownRequest,
  type PutOnHoldRequest,
  type ResumeFromHoldRequest,
  type SalesSowingInstructionRequest,
  type SubmitSowingInstructionRequest,
  type AcceptSowingInstructionRequest,
  type AcknowledgeReceiptRequest,
  type RecordMaterialUsageRequest,
} from "../services/sowingInstructionsApi";

// ─── Helper Functions ───────────────────────────────────────────────────────

/**
 * Centralised cache invalidation for mutations that affect the production
 * pipeline. Covers SowingInstructions list, the per-entry stock pipeline
 * (Growth / Mark Ready / Ready for Dispatch tabs), and the greenhouse stock
 * page so each surface picks up changes without a manual refresh.
 */
const invalidateProductionPipeline = () => {
  queryClient.invalidateQueries({ queryKey: ["sowingInstructions"] });
  queryClient.invalidateQueries({ queryKey: ["orders", "ready-for-sowing"] });
  queryClient.invalidateQueries({ queryKey: ["sowing-lifecycle"] });
  queryClient.invalidateQueries({ queryKey: ["greenhouse-stock-entries"] });
  queryClient.invalidateQueries({ queryKey: ["greenhouse-stock"] });
  queryClient.invalidateQueries({ queryKey: ["greenhouse-stock-available"] });
};

const STATUS_MAP: Record<string, number> = {
  Pending: 0,
  Issued: 1,
  Sown: 2,
  Germinated: 3,
  Growing: 4,
  ReadyForDispatch: 5,
  "Ready for Dispatch": 5, // legacy label alias
  "Ready for Harvest": 5, // legacy label alias
  Stored: 6,
  Dispatched: 7,
  Overgrown: 8,
  Revoked: 9,
  OnHold: 10,
  "On Hold": 10, // legacy label alias
  ReadyForGrowing: 11,
  "Ready for Growing": 11, // legacy label alias
  Draft: 12,
  PartiallySown: 13,
  "Partially Sown": 13, // legacy label alias
  Rejected: 14,
};

// The API serialises enums as their member names (e.g. "Trays", "PerTray"), but
// the UI works in the numeric values. Normalise everything the same way status
// is normalised so bracket/label comparisons against numbers keep working.
const UNIT_MAP: Record<string, number> = {
  Unspecified: 0,
  Kilograms: 1,
  Kilogram: 1,
  Grams: 2,
  Gram: 2,
  Liters: 3,
  Liter: 3,
  Milliliters: 4,
  Milliliter: 4,
  Pieces: 5,
  Piece: 5,
  Boxes: 6,
  Box: 6,
  Bags: 7,
  Bag: 7,
  Packets: 8,
  Packet: 8,
  Bottles: 9,
  Bottle: 9,
  Cans: 10,
  Can: 10,
  Trays: 11,
  Tray: 11,
  SeedCount: 12,
};

const PRICING_MODE_MAP: Record<string, number> = {
  PerTray: 1,
  PerSeedlingOrPiece: 2,
};

const TRAY_TYPE_MAP: Record<string, number> = {
  TwoTenHole: 210,
  FourFortyTwoHole: 442,
};

const normalizeStatusString = (status: string | number): number => {
  if (typeof status === "number") return status;
  return STATUS_MAP[status] ?? 0;
};

const normalizeUnit = (unit: string | number): number => {
  if (typeof unit === "number") return unit;
  return UNIT_MAP[unit] ?? 0;
};

const normalizeOptionalEnum = (
  value: string | number | null | undefined,
  map: Record<string, number>
): number | undefined => {
  if (value === null || value === undefined) return undefined;
  return typeof value === "number" ? value : map[value];
};

const normalizeSowingInstruction = <
  T extends {
    status: string | number;
    unit: string | number;
    pricingMode?: string | number | null;
    trayType?: string | number | null;
  },
>(
  inst: T
) => ({
  ...inst,
  status: normalizeStatusString(inst.status),
  unit: normalizeUnit(inst.unit),
  pricingMode: normalizeOptionalEnum(inst.pricingMode, PRICING_MODE_MAP),
  trayType: normalizeOptionalEnum(inst.trayType, TRAY_TYPE_MAP),
});

// ─── Queries ────────────────────────────────────────────────────────────────

export const useAllSowingInstructions = () => {
  return useQuery({
    queryKey: ["sowingInstructions"],
    queryFn: async () => {
      const instructions = await sowingInstructionsApi.getAllSowingInstructions();
      return instructions.map(normalizeSowingInstruction);
    },
  });
};

export const useSowingInstructionsByStatus = (status: number | number[], disabled = false) => {
  const statuses = Array.isArray(status) ? status : [status];
  return useQuery({
    queryKey: ["sowingInstructions", "status", ...statuses],
    queryFn: async () => {
      const results = await Promise.all(
        statuses.map((s) => sowingInstructionsApi.getSowingInstructionsByStatus(s))
      );
      const flat = results.flat();
      return flat.map(normalizeSowingInstruction);
    },
    enabled: !disabled && statuses.length > 0,
  });
};

// Server-paged variant used by the Sowing Management page. Keeps page state in
// the query key so React Query caches each page independently and refetches
// only when status filter, page, size, or search change.
export const usePagedSowingInstructionsByStatuses = (
  status: number | number[],
  pageNumber: number,
  pageSize: number,
  search: string,
  disabled = false,
  includeUnfinishedSowing = false,
  includeUngerminatedSowings = false,
  requirePhysicalGreenhouseStock = false
) => {
  const statuses = Array.isArray(status) ? status : [status];
  return useQuery({
    queryKey: [
      "sowingInstructions",
      "paged",
      [...statuses].sort(),
      pageNumber,
      pageSize,
      search,
      includeUnfinishedSowing,
      includeUngerminatedSowings,
      requirePhysicalGreenhouseStock,
    ],
    queryFn: async () => {
      const page = await sowingInstructionsApi.getPagedSowingInstructionsByStatuses(
        statuses,
        pageNumber,
        pageSize,
        search || undefined,
        includeUnfinishedSowing,
        includeUngerminatedSowings,
        requirePhysicalGreenhouseStock
      );
      return {
        ...page,
        items: page.items.map(normalizeSowingInstruction),
      };
    },
    enabled: !disabled && statuses.length > 0,
    placeholderData: (prev) => prev,
  });
};

export const useSowingInstructionsByOrderId = (orderId: string) => {
  return useQuery({
    queryKey: ["sowingInstructions", "order", orderId],
    queryFn: async () => {
      const instructions = await sowingInstructionsApi.getSowingInstructionsByOrderId(orderId);
      return instructions.map(normalizeSowingInstruction);
    },
    enabled: !!orderId,
  });
};

export const useSowingInstructionById = (id: string) => {
  return useQuery({
    queryKey: ["sowingInstructions", id],
    queryFn: async () => {
      const instruction = await sowingInstructionsApi.getSowingInstructionById(id);
      return normalizeSowingInstruction(instruction);
    },
    enabled: !!id,
  });
};

export const useSowingInstructionByNumber = (instructionNumber: string) => {
  return useQuery({
    queryKey: ["sowingInstructions", "number", instructionNumber],
    queryFn: async () => {
      const instruction = await sowingInstructionsApi.getSowingInstructionByNumber(instructionNumber);
      return normalizeSowingInstruction(instruction);
    },
    enabled: !!instructionNumber,
  });
};

// ─── Mutations ───────────────────────────────────────────────────────────────

export const useCreateSowingInstruction = () => {
  return useMutation({
    mutationFn: ({ idempotencyKey, ...payload }: CreateSowingInstructionRequest & { idempotencyKey?: string }) =>
      sowingInstructionsApi.createSowingInstruction(payload, idempotencyKey),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["sowingInstructions"],
        exact: false,
      });
      queryClient.invalidateQueries({ queryKey: ["orders", "ready-for-sowing"] });
    },
  });
};

export const useCreateBatchSowingInstructions = () => {
  return useMutation({
    mutationFn: ({ idempotencyKey, ...payload }: CreateBatchSowingInstructionRequest & { idempotencyKey?: string }) =>
      sowingInstructionsApi.createBatchSowingInstructions(payload, idempotencyKey),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["sowingInstructions"],
        exact: false,
      });
      queryClient.invalidateQueries({ queryKey: ["orders", "ready-for-sowing"] });
    },
  });
};

export const useApproveSowingInstruction = () => {
  return useMutation({
    mutationFn: (payload: ApproveSowingInstructionRequest) =>
      sowingInstructionsApi.approveSowingInstruction(payload),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["sowingInstructions"] });
      queryClient.invalidateQueries({
        queryKey: ["sowingInstructions", data.id],
      });
    },
  });
};

export const useRevokeSowingInstruction = () => {
  return useMutation({
    mutationFn: (payload: RevokeSowingInstructionRequest) =>
      sowingInstructionsApi.revokeSowingInstruction(payload),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["sowingInstructions"] });
      queryClient.invalidateQueries({
        queryKey: ["sowingInstructions", data.id],
      });
    },
  });
};

export const useUpdateSowingInstruction = () => {
  return useMutation({
    mutationFn: (payload: import("../services/sowingInstructionsApi").UpdateSowingInstructionRequest) =>
      sowingInstructionsApi.updateSowingInstruction(payload),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["sowingInstructions"] });
      queryClient.invalidateQueries({
        queryKey: ["sowingInstructions", data.id],
      });
    },
  });
};

export const useRecordSowing = () => {
  return useMutation({
    mutationFn: ({ idempotencyKey, ...payload }: RecordSowingRequest & { idempotencyKey?: string }) =>
      sowingInstructionsApi.recordSowing(payload, idempotencyKey),
    onSuccess: () => {
      invalidateProductionPipeline();
    },
  });
};

export const useRecordGermination = () => {
  return useMutation({
    mutationFn: (payload: RecordGerminationRequest) =>
      sowingInstructionsApi.recordGermination(payload),
    onSuccess: () => {
      invalidateProductionPipeline();
    },
  });
};

export const useRecordGrowth = () => {
  return useMutation({
    mutationFn: (payload: RecordGrowthRequest) =>
      sowingInstructionsApi.recordGrowth(payload),
    onSuccess: () => {
      invalidateProductionPipeline();
    },
  });
};

export const useRecordReadyForDispatch = () => {
  return useMutation({
    mutationFn: (payload: RecordReadyForDispatchRequest) =>
      sowingInstructionsApi.recordReadyForDispatch(payload),
    onSuccess: () => {
      invalidateProductionPipeline();
      // Marking ready for dispatch advances the linked order to ProductionCompleted (6)
      queryClient.invalidateQueries({ queryKey: ["orders", "status", 6] });
      queryClient.invalidateQueries({ queryKey: ["orders"] });
    },
  });
};

export const useRecordHarvest = () => {
  return useMutation({
    mutationFn: (payload: RecordHarvestRequest) =>
      sowingInstructionsApi.recordHarvest(payload),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["sowingInstructions"] });
      queryClient.invalidateQueries({
        queryKey: ["sowingInstructions", data.id],
      });
    },
  });
};

export const useMarkOvergrown = () => {
  return useMutation({
    mutationFn: (payload: MarkOvergrownRequest) =>
      sowingInstructionsApi.markOvergrown(payload),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["sowingInstructions"] });
      queryClient.invalidateQueries({
        queryKey: ["sowingInstructions", data.id],
      });
    },
  });
};

export const usePutOnHold = () => {
  return useMutation({
    mutationFn: (payload: PutOnHoldRequest) =>
      sowingInstructionsApi.putOnHold(payload),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["sowingInstructions"] });
      queryClient.invalidateQueries({
        queryKey: ["sowingInstructions", data.id],
      });
    },
  });
};

export const useResumeFromHold = () => {
  return useMutation({
    mutationFn: (payload: ResumeFromHoldRequest) =>
      sowingInstructionsApi.resumeFromHold(payload),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["sowingInstructions"] });
      queryClient.invalidateQueries({
        queryKey: ["sowingInstructions", data.id],
      });
    },
  });
};

// ─── Phase-workflow hooks ────────────────────────────────────────────────────

export const usePendingAcceptanceQueue = () =>
  useQuery({
    queryKey: ["sowingInstructions", "pending-acceptance"],
    queryFn: () => sowingInstructionsApi.getPendingAcceptanceQueue(),
  });

export const useReadyForGrowingQueue = () =>
  useQuery({
    queryKey: ["sowingInstructions", "ready-for-growing"],
    queryFn: () => sowingInstructionsApi.getReadyForGrowingQueue(),
  });

export const useGerminationSplitPreview = (id: string, qty: number) =>
  useQuery({
    queryKey: ["germination-split-preview", id, qty],
    queryFn: () => sowingInstructionsApi.getGerminationSplitPreview(id, qty),
    enabled: !!id && qty > 0,
  });

export const useGerminationChambers = () =>
  useQuery({
    queryKey: ["germination-chambers"],
    queryFn: () => sowingInstructionsApi.getGerminationChambers(),
  });

export const useSalesCreateDraft = () =>
  useMutation({
    mutationFn: (payload: SalesSowingInstructionRequest) =>
      sowingInstructionsApi.salesCreateDraft(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sowingInstructions"] });
    },
  });

export const useSubmitSowingInstruction = () =>
  useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: SubmitSowingInstructionRequest }) =>
      sowingInstructionsApi.submitSowingInstruction(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sowingInstructions"] });
    },
  });

export const useAcceptSowingInstruction = () =>
  useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: AcceptSowingInstructionRequest }) =>
      sowingInstructionsApi.acceptSowingInstruction(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sowingInstructions"] });
      queryClient.invalidateQueries({ queryKey: ["sowingInstructions", "pending-acceptance"] });
    },
  });

export const useAcknowledgeReceipt = () =>
  useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: AcknowledgeReceiptRequest }) =>
      sowingInstructionsApi.acknowledgeReceipt(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sowingInstructions"] });
      queryClient.invalidateQueries({ queryKey: ["sowingInstructions", "ready-for-growing"] });
      queryClient.invalidateQueries({ queryKey: ["greenhouse-stock-entries"] });
      // Bed CurrentOccupancy bumped — refresh bed pickers so the next dialog reflects it.
      queryClient.invalidateQueries({ queryKey: ["greenhouses"] });
      queryClient.invalidateQueries({ queryKey: ["sowing-lifecycle", "available-beds"] });
    },
  });

export const useRecordMaterialUsage = () =>
  useMutation({
    mutationFn: (payload: RecordMaterialUsageRequest) =>
      sowingInstructionsApi.recordMaterialUsage(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sowingInstructions"] });
      queryClient.invalidateQueries({ queryKey: ["material-usage"] });
    },
  });

export const useMaterialUsageByDate = (
  date: string,
  options?: { mineOnly?: boolean; departmentId?: string; recordedByUserId?: string }
) =>
  useQuery({
    queryKey: ["material-usage", date, options],
    queryFn: () => sowingInstructionsApi.getMaterialUsageByDate(date, options),
    enabled: !!date,
  });

// ── Bulk-import hooks (greenhouse stock at go-live) ──────────────────────
export const useDownloadStockImportTemplate = () =>
  useMutation({
    mutationFn: () => sowingInstructionsApi.downloadImportTemplate(),
  });

export const useImportStock = () =>
  useMutation({
    mutationFn: (file: File) => sowingInstructionsApi.importStock(file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sowingInstructions"] });
      queryClient.invalidateQueries({ queryKey: ["greenhouse-stock-entries"] });
    },
  });
